import { toast } from '@cyphex/ui';
import type { NotificationItem } from './types';

const URGENT_MS = 12_000;
const NORMAL_MS = 5000;

/**
 * The app-wide toast API. Everything routes through here so copy stays short
 * and no raw API error ever reaches a user.
 */
export const notify = {
  success: (message: string, description?: string) => toast.success(message, { description }),
  error: (message: string, description?: string) => toast.error(message, { description }),
  info: (message: string, description?: string) => toast.info(message, { description }),
  /**
   * `options.id` IS NOT COSMETIC. Sonner creates a new toast per call unless an
   * id is reused, so a warning raised on a repeating event stacks — a global
   * hotkey held down once put 83 identical toasts on screen, in two windows
   * each, and the queue outlived the key press by about a minute. A caller that
   * can fire more than once for one user gesture passes a stable id and gets
   * one toast that updates in place.
   */
  warning: (message: string, description?: string, options?: { id?: string }) =>
    toast.warning(message, { description, ...(options?.id === undefined ? {} : { id: options.id }) }),
  dismiss: (id?: string | number) => toast.dismiss(id),

  /** Generic activity toast for kinds that do not warrant the message popup. */
  activity(item: NotificationItem, onActivate: (item: NotificationItem) => void): void {
    /*
     * NO KIND PREFIX. The server writes titles that already read as sentences —
     * "Incoming call from Vamshi Uppala", "Ganesh reacted to your message" —
     * so prepending the kind label produced "Incoming call: Incoming call from
     * Vamshi Uppala". The label belongs on the notification ROW, where it is a
     * column heading, not in front of a sentence that already contains it.
     */
    toast(item.title, {
      id: item.groupKey ?? item.id,
      description: item.preview.snippet ?? item.body,
      duration: item.priority === 'urgent' ? URGENT_MS : NORMAL_MS,
      ...(item.deepLink
        ? { action: { label: 'Open', onClick: () => onActivate(item) } }
        : {}),
    });
  },
};
